import { useAction, usePersistentScroll } from '@agro-club/frontend-shared'
import useLangPicker from 'hooks/useLangPicker'
import AuthSelectors from 'modules/domain/auth/selectors'
import PedigreedGrowerTargetSkuActions from 'modules/domain/target2sku/pedigreedGrowerTarget/duck'
import { usePedigreedGrowerTargetSkuList } from 'modules/domain/target2sku/pedigreedGrowerTarget/hooks'
import PedigreedGrowerTargetSkuSelectors from 'modules/domain/target2sku/pedigreedGrowerTarget/selectors'
import { CAPABILITY, PERMISSION, usePermissions } from 'modules/permissions/permissions'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { isProducer } from 'types/entities'
import { CompoundedTableEditable } from 'views/components/CompoundedTable/CompoundedTableEditable/CompoundedTableEditable'
import { PedigreedGrowerTargetSkuForm } from '../forms/PedigreedGrowerTargetSkuForm'
import { BolderCell, TertiaryCell, TitleCell, TitleI18nCell } from './common'
import { TextTertiary } from '../styles'
import useCompareStrings from 'hooks/useCompareStrings'

export const PedigreedGrowerTargetSkuTable: React.VFC = () => {
  const { t } = useTranslation('target2')
  const { pick } = useLangPicker()

  const [progress, data = []] = usePedigreedGrowerTargetSkuList()

  const total = useSelector(PedigreedGrowerTargetSkuSelectors.total)
  const page = useSelector(PedigreedGrowerTargetSkuSelectors.page)
  const pages = useSelector(PedigreedGrowerTargetSkuSelectors.pages)
  const pageSize = useSelector(PedigreedGrowerTargetSkuSelectors.pageSize)
  const targetsTotal = useSelector(PedigreedGrowerTargetSkuSelectors.targetsTotal)
  const farmerOrdersTotal = useSelector(PedigreedGrowerTargetSkuSelectors.farmerOrdersTotal)
  const previousYear = useSelector(PedigreedGrowerTargetSkuSelectors.previousYear)

  const listRequested = useAction(PedigreedGrowerTargetSkuActions.listRequested)
  const { scrollRef } = usePersistentScroll('pedigreedGrowerTargetSkuList')
  const role = useSelector(AuthSelectors.role)
  const producer = isProducer(role)
  const compareStrings = useCompareStrings()

  const columns = useMemo(
    () => [
      {
        Header: t('list.tableHeaders.manufacturer'),
        accessor: 'manufacturer' as const,
        width: 193,
        hidden: producer,
        Cell: TitleCell,
        sortType: compareStrings,
      },
      {
        Header: t('list.tableHeaders.territory'),
        accessor: 'territory' as const,
        width: 193,
        Cell: TitleCell,
        sortType: compareStrings,
      },
      {
        Header: t('list.tableHeaders.season'),
        accessor: 'season' as const,
        width: 193,
        Cell: TitleCell,
        sortType: compareStrings,
      },
      {
        Header: t('list.tableHeaders.grower'),
        accessor: 'grower' as const,
        width: 213,
        Cell: TitleCell,
        sortType: compareStrings,
      },
      {
        Header: t('list.tableHeaders.subcategory'),
        accessor: 'subcategory' as const,
        width: 97,
        Cell: TitleI18nCell(pick),
        sortType: compareStrings,
      },
      {
        Header: t('list.tableHeaders.product'),
        accessor: 'product' as const,
        width: 129,
        Cell: TitleI18nCell(pick),
        sortType: compareStrings,
      },
      {
        Header: t('list.tableHeaders.growerTarget'),
        accessor: 'target_value' as const,
        width: 80,
        headerAlign: 'right',
        cellAlign: 'right',
        Cell: BolderCell,
      },
      {
        Header: t('list.tableHeaders.seedGrowerOrders'),
        accessor: 'farmer_orders_sku' as const,
        width: 80,
        headerAlign: 'right',
        cellAlign: 'right',
      },
      {
        Header: <TextTertiary>{t('list.tableHeaders.seedGrowerPreviousYear')}</TextTertiary>,
        accessor: 'farmer_orders_sku_previous_year' as const,
        width: 76,
        Cell: TertiaryCell,
        headerAlign: 'right',
        cellAlign: 'right',
      },
    ],
    [t, producer, pick, compareStrings],
  )

  const metaColumns = useMemo(
    () => ({
      target_value: targetsTotal,
      farmer_orders_sku: farmerOrdersTotal,
      farmer_orders_sku_previous_year: <TextTertiary>{previousYear}</TextTertiary>,
    }),
    [targetsTotal, farmerOrdersTotal, previousYear],
  )

  const canEdit = usePermissions({ capability: CAPABILITY.PEDIGREED_GROWER_TARGETS, permission: PERMISSION.CRU })

  return (
    <CompoundedTableEditable
      FormComponent={PedigreedGrowerTargetSkuForm}
      columns={columns}
      metaColumns={metaColumns}
      data={data}
      ref={scrollRef}
      progress={progress}
      total={total}
      currentPage={page}
      pageSize={pageSize}
      onSetPage={(num) => {
        listRequested({ page: num })
      }}
      pages={pages}
      readonly={!canEdit}
      newTitle={t('form.new')}
      isSortable
      defaultSortField="grower"
    />
  )
}

export default PedigreedGrowerTargetSkuTable
